import { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/Button";
import { formatAmount } from "@/utils/formatAmount";
import {
  useFrameworkAgreements,
  useScenarioContractors,
  useScenarioLineItems,
  useScenarioMutations,
  useScenarios,
} from "../hooks";
import { geocodeBuildLocation } from "../api";
import type { BuildLocationGeocodeHit } from "../api";
import { CATEGORY_LABELS } from "../types";
import type {
  EstimateJobDetail,
  PositionCategory,
  SubCostSnapshot,
  SubcontractorAssignment,
  SubcontractorScenario,
} from "../types";
import { BuildLocationMap } from "./BuildLocationMap";
import { NO_GROUP_KEY, groupRows } from "@/features/contractors/agreementGroups";

function money(v?: string | null): string {
  if (v == null) return "—";
  const n = Number(v);
  return Number.isNaN(n) ? v : formatAmount(n);
}

interface FlatPos {
  id: string;
  ordinal: number;
  description: string;
  category: PositionCategory;
}

type Mut = ReturnType<typeof useScenarioMutations>;

const CATEGORY_ORDER: PositionCategory[] = ["budowlana", "ppn", "biuro", "inne"];

// ── Margin / breakdown summary ───────────────────────────────────────────────
function StatCard({
  label,
  value,
  tone,
  sub,
}: {
  label: string;
  value: string;
  tone?: "neutral" | "sub" | "margin" | "loss";
  sub?: string;
}) {
  const cls =
    tone === "margin"
      ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)]"
      : tone === "loss"
        ? "border-red-400 bg-red-50"
        : "border-[var(--color-border)] bg-[var(--color-bg-surface)]";
  const num =
    tone === "margin"
      ? "text-[var(--color-primary)]"
      : tone === "loss"
        ? "text-red-600"
        : "text-[var(--color-text-primary)]";
  return (
    <div className={`p-4 border ${cls}`}>
      <div className="section-label">{label}</div>
      <div className={`mt-1 text-2xl font-semibold tabular-nums ${num}`}>
        {money(value)}{" "}
        <span className="text-sm font-normal text-[var(--color-text-muted)]">zł</span>
      </div>
      {sub && (
        <div className="text-[11px] text-[var(--color-text-secondary)] mt-1 tabular-nums">
          {sub}
        </div>
      )}
    </div>
  );
}

function MarginPanel({ snap }: { snap: SubCostSnapshot }) {
  const margin = Number(snap.margin);
  const cov = Math.min(100, Number(snap.coverage_pct) || 0);
  const maxContractor = Math.max(
    1,
    ...snap.by_contractor.map((c) => Number(c.cost) || 0),
  );
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-3">
        <StatCard label="Nasza wycena (pokryte)" value={snap.operator_covered} />
        <StatCard label="Koszt podwykonawców" value={snap.subcontractor_total} tone="sub" />
        <StatCard
          label="Marża (nasza − podwykonawcy)"
          value={snap.margin}
          tone={margin < 0 ? "loss" : "margin"}
          sub={`${snap.margin_pct}% marży`}
        />
      </div>

      <div>
        <div className="flex justify-between text-[11px] text-[var(--color-text-secondary)] mb-1">
          <span>Pokrycie zakresu przez podwykonawców</span>
          <span className="tabular-nums">
            {snap.coverage_pct}% · niepokryte {money(snap.uncovered_value)} zł
          </span>
        </div>
        <div className="h-2.5 bg-[var(--color-bg-elevated)] overflow-hidden">
          <div className="h-full bg-[var(--color-primary)]" style={{ width: `${cov}%` }} />
        </div>
      </div>

      {/* Ilości liczone jako 0 do czasu ręcznego uzupełnienia — koszt jest ZANIŻONY,
          więc marża obok niego jest na razie nieprawdziwa. */}
      {(snap.needs_quantity ?? 0) > 0 && (
        <div className="text-[11px] text-red-700 bg-red-50 border border-red-200 p-2">
          <span className="font-bold">!</span> {snap.needs_quantity} przypisań czeka na ręczne
          uzupełnienie ilości (jednostka cennika inna niż obmiar naszych pozycji, np. szt. vs m) —
          liczone jako 0, więc koszt podwykonawców i marża są zaniżone.
        </div>
      )}

      {snap.warnings?.length > 0 && (
        <ul className="text-[11px] text-amber-800 bg-amber-50 border border-amber-200 p-2 space-y-0.5">
          {snap.warnings.map((w, i) => (
            <li key={i}>⚠ {w}</li>
          ))}
        </ul>
      )}

      <div className="grid grid-cols-2 gap-5">
        {snap.by_contractor.length > 0 && (
          <div className="card p-3">
            <div className="section-label mb-2">Koszt wg podwykonawcy</div>
            <div className="space-y-1.5">
              {snap.by_contractor.map((c) => (
                <div key={c.contractor_id} className="text-xs">
                  <div className="flex justify-between mb-0.5">
                    <span className="truncate">{c.name}</span>
                    <span className="tabular-nums font-medium whitespace-nowrap">
                      {money(c.cost)} zł
                    </span>
                  </div>
                  <div className="h-1.5 bg-[var(--color-bg-elevated)]">
                    <div
                      className="h-full bg-[var(--color-text-secondary)]"
                      style={{ width: `${(Number(c.cost) / maxContractor) * 100}%` }}
                    />
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
        {Object.keys(snap.by_category).length > 0 && (
          <div className="card p-3">
            <div className="section-label mb-2">Marża wg kategorii</div>
            <table className="w-full text-xs">
              <thead className="text-[var(--color-text-muted)]">
                <tr>
                  <th className="text-left font-medium pb-1">Kategoria</th>
                  <th className="text-right font-medium pb-1">Nasza</th>
                  <th className="text-right font-medium pb-1">Sub</th>
                  <th className="text-right font-medium pb-1">Marża</th>
                </tr>
              </thead>
              <tbody>
                {CATEGORY_ORDER.filter((c) => snap.by_category[c]).map((c) => {
                  const r = snap.by_category[c]!;
                  const m = Number(r.margin);
                  return (
                    <tr key={c} className="border-t border-[var(--color-border)]">
                      <td className="py-1">{CATEGORY_LABELS[c]}</td>
                      <td className="text-right tabular-nums">{money(r.operator)}</td>
                      <td className="text-right tabular-nums">{money(r.sub)}</td>
                      <td
                        className={`text-right tabular-nums font-medium ${
                          m < 0 ? "text-red-600" : "text-[var(--color-text-primary)]"
                        }`}
                      >
                        {money(r.margin)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {snap.uncovered_positions.length > 0 && (
        <div className="card p-3 border-amber-200">
          <div className="section-label mb-1 text-amber-700">
            Niepokryte przez podwykonawców — robimy sami / do dobrania (
            {snap.uncovered_positions.length})
          </div>
          <ul className="text-[11px] text-[var(--color-text-secondary)] max-h-32 overflow-auto divide-y divide-[var(--color-border)]">
            {snap.uncovered_positions.map((u) => (
              <li key={u.ordinal} className="py-1 flex justify-between gap-3">
                <span>
                  <span className="text-[var(--color-text-muted)]">poz. {u.ordinal}:</span>{" "}
                  {u.description}
                  {u.partial && (
                    <span
                      className="ml-1 text-[var(--color-text-muted)]"
                      title="Pozycja podzielona między podwykonawców — to jest reszta, której nikt nie bierze"
                    >
                      (reszta po podziale)
                    </span>
                  )}
                </span>
                <span className="tabular-nums whitespace-nowrap">{money(u.value)} zł</span>
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

// ── Coverage chips (shared) ──────────────────────────────────────────────────
function CoverageChips({
  a,
  positions,
  mut,
  canEdit,
}: {
  a: SubcontractorAssignment;
  positions: FlatPos[];
  mut: Mut;
  canEdit: boolean;
}) {
  const [addPos, setAddPos] = useState("");
  // Podział pozycji: wpisujemy KWOTĘ naszej wartości, którą bierze ten
  // podwykonawca („budowlance 3 000 zł, resztę PPN”). Serwer przelicza ją na
  // udział zakresu, więc po zmianie narzutów podział zostaje ten sam.
  const [splitting, setSplitting] = useState<string | null>(null);
  const [amount, setAmount] = useState("");
  const coveredIds = new Set(a.coverages.map((c) => c.position));
  const saveSplit = (positionId: string) =>
    mut.setCoverage.mutate(
      { aid: a.id, position_id: positionId, amount },
      { onSuccess: () => setSplitting(null) },
    );
  return (
    <div>
      <div className="flex flex-wrap gap-1">
        {a.coverages.map((c) => (
          <span
            key={c.id}
            title={c.position_description}
            className="inline-flex items-center gap-1 bg-[var(--color-bg-elevated)] px-1.5 py-0.5 text-[10px]"
          >
            poz. {c.position_ordinal}
            {splitting === c.id ? (
              <>
                <input
                  autoFocus
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === "Enter") saveSplit(c.position);
                    if (e.key === "Escape") setSplitting(null);
                  }}
                  className="w-16 border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-1 text-[10px] tabular-nums"
                />
                <span className="text-[var(--color-text-muted)]">
                  zł z {money(c.position_value)}
                </span>
                <button
                  className="text-[var(--color-primary)] hover:underline"
                  disabled={mut.setCoverage.isPending}
                  onClick={() => saveSplit(c.position)}
                >
                  zapisz
                </button>
                <button
                  className="text-[var(--color-text-muted)] hover:underline"
                  onClick={() => setSplitting(null)}
                >
                  anuluj
                </button>
              </>
            ) : (
              <>
                {Number(c.fraction) !== 1 &&
                  ` · ${money(c.amount)} zł z ${money(c.position_value)} (${Math.round(
                    Number(c.fraction) * 100,
                  )}%)`}
                {canEdit && (
                  <button
                    className="text-[var(--color-primary)] hover:underline"
                    title="Podziel pozycję — wpisz, ile złotych naszej wartości bierze ten podwykonawca; resztę przypisz drugiemu"
                    onClick={() => {
                      setAmount(String(Number(c.amount)));
                      setSplitting(c.id);
                    }}
                  >
                    {Number(c.fraction) === 1 ? "podziel" : "zmień"}
                  </button>
                )}
                {canEdit && (
                  <button
                    className="text-red-500 hover:text-red-700"
                    onClick={() => mut.removeCoverage.mutate({ aid: a.id, position_id: c.position })}
                  >
                    ×
                  </button>
                )}
              </>
            )}
          </span>
        ))}
      </div>
      {canEdit && (
        <select
          value={addPos}
          onChange={(e) => {
            if (e.target.value) {
              mut.setCoverage.mutate({ aid: a.id, position_id: e.target.value });
              setAddPos("");
            }
          }}
          className="mt-1 text-[10px] bg-transparent text-[var(--color-primary)] border-0 p-0 cursor-pointer"
        >
          <option value="">+ dodaj pozycję…</option>
          {positions
            .filter((p) => !coveredIds.has(p.id))
            .map((p) => (
              <option key={p.id} value={p.id}>
                poz. {p.ordinal}: {p.description.slice(0, 40)}
              </option>
            ))}
        </select>
      )}
    </div>
  );
}

function ContractorTag({ name }: { name: string | null }) {
  return (
    <span className="inline-block bg-[var(--color-bg-elevated)] text-[var(--color-text-secondary)] px-1.5 py-0.5 text-[10px]">
      {name || "(bez podwykonawcy)"}
    </span>
  );
}

// ── Active assignment row ────────────────────────────────────────────────────
function AssignmentRow({
  a,
  positions,
  mut,
  canEdit,
}: {
  a: SubcontractorAssignment;
  positions: FlatPos[];
  mut: Mut;
  canEdit: boolean;
}) {
  const [qty, setQty] = useState(a.quantity);
  useEffect(() => setQty(a.quantity), [a.quantity]);
  const [showWhy, setShowWhy] = useState(false);
  const qtyAlert = a.quantity_alert || "";
  return (
    <tr className="border-t border-[var(--color-border)] align-top hover:bg-[var(--color-bg-base)]">
      <td className="px-2 py-2 text-xs">
        <div className="font-medium text-[var(--color-text-primary)]">{a.description}</div>
        <div className="mt-0.5 flex items-center gap-1.5 flex-wrap">
          <ContractorTag name={a.contractor_name} />
          {a.match_status === "ai" && (
            <span className="text-[10px] text-amber-700">AI {a.match_score.toFixed(0)}%</span>
          )}
          {a.ai_rationale && (
            <button
              className="text-[10px] text-[var(--color-primary)] hover:underline"
              onClick={() => setShowWhy((s) => !s)}
            >
              {showWhy ? "ukryj" : "dlaczego?"}
            </button>
          )}
        </div>
        {showWhy && a.ai_rationale && (
          <div className="mt-1 text-[10px] text-[var(--color-text-secondary)] italic max-w-[300px]">
            {a.ai_rationale}
          </div>
        )}
      </td>
      <td className="px-2 py-2 text-xs whitespace-nowrap">
        {a.value_multiplier != null ? (
          <span className="text-[11px] text-[var(--color-text-secondary)]">
            <span className="text-[var(--color-primary)]">reguła słowna</span> · Σ naszej wartości
            pokrytych poz. ×{" "}
            <span className="font-semibold tabular-nums text-[var(--color-text-primary)]">
              {String(a.value_multiplier).replace(".", ",")}
            </span>
          </span>
        ) : (
          <>
            {/* Ilość, której nie da się wyprowadzić z naszego obmiaru (cennik w szt.,
                przedmiar w m) — czerwony wykrzyknik zamiast zgadywanej liczby. */}
            {qtyAlert && (
              <span
                className="mr-1 font-bold text-red-600 cursor-help"
                title={qtyAlert}
                aria-label={qtyAlert}
              >
                !
              </span>
            )}
            {canEdit ? (
              <input
                value={qty}
                onChange={(e) => setQty(e.target.value)}
                onBlur={() => {
                  if (qty !== a.quantity) mut.patchAssignment.mutate({ aid: a.id, quantity: qty });
                }}
                className={`w-16 border bg-[var(--color-bg-surface)] px-1 py-0.5 text-right tabular-nums ${
                  qtyAlert ? "border-red-500 text-red-700" : "border-[var(--color-border)]"
                }`}
              />
            ) : (
              <span className={`tabular-nums ${qtyAlert ? "text-red-700" : ""}`}>
                {money(a.quantity)}
              </span>
            )}{" "}
            {a.is_tiered ? (
              <span
                className="text-[10px] text-[var(--color-text-secondary)] cursor-help"
                title="Ilość = długość trasy L w metrach. Rozpoczęty odcinek liczy się jak pełny — 27 m kosztuje tyle co 100 m."
              >
                <span className="text-[var(--color-primary)]">schodkowo</span> · L [m] ·{" "}
                {Number(a.rate) === Number(a.tier_subsequent_rate) ? (
                  <>
                    każde rozpoczęte {String(Number(a.tier_step))} → {money(a.rate)}
                  </>
                ) : (
                  <>
                    pierwsze {money(a.rate)} + każde kolejne rozpoczęte{" "}
                    {String(Number(a.tier_step))} → {money(a.tier_subsequent_rate)}
                  </>
                )}
              </span>
            ) : (
              <>
                {a.unit} × {money(a.rate)}
                {a.use_high_priority && (
                  <span className="ml-1 text-[10px] text-[var(--color-primary)]">
                    (priorytetowa)
                  </span>
                )}
                {a.travel_free_km != null && Number(a.travel_free_km) > 0 && (
                  <span className="ml-1 text-[10px] text-[var(--color-text-secondary)]">
                    (darmowy do {String(a.travel_free_km).replace(".", ",")} km)
                  </span>
                )}
              </>
            )}
            {qtyAlert && (
              <div className="mt-1 max-w-[240px] whitespace-normal text-[10px] text-red-600">
                {qtyAlert}
              </div>
            )}
          </>
        )}
      </td>
      <td className="px-2 py-2 text-xs text-right font-semibold tabular-nums whitespace-nowrap text-[var(--color-text-primary)]">
        {money(a.cost)} zł
      </td>
      <td className="px-2 py-2 text-xs">
        <CoverageChips a={a} positions={positions} mut={mut} canEdit={canEdit} />
      </td>
      <td className="px-2 py-2 text-right whitespace-nowrap">
        {canEdit && (
          <>
            <button
              className="text-[11px] text-[var(--color-text-secondary)] hover:text-[var(--color-primary)] hover:underline"
              title="Przenieś do wariantów (oferta konkurencyjna — przestanie liczyć się do kosztu)"
              onClick={() => mut.patchAssignment.mutate({ aid: a.id, is_alternative: true })}
            >
              → wariant
            </button>
            <button
              className="ml-2 text-[11px] text-red-500 hover:underline"
              onClick={() => mut.deleteAssignment.mutate(a.id)}
            >
              usuń
            </button>
          </>
        )}
      </td>
    </tr>
  );
}

// ── Alternatives (warianty) panel ────────────────────────────────────────────
function AlternativesPanel({
  alts,
  mut,
  canEdit,
}: {
  alts: SubcontractorAssignment[];
  mut: Mut;
  canEdit: boolean;
}) {
  const [open, setOpen] = useState(false);
  if (alts.length === 0) return null;
  return (
    <div className="border border-dashed border-[var(--color-border-strong)] bg-[var(--color-bg-base)]">
      <button
        className="w-full px-3 py-2 flex items-center justify-between text-left"
        onClick={() => setOpen((o) => !o)}
      >
        <span className="section-label flex items-center gap-2">
          <span className="text-[var(--color-text-muted)]">{open ? "▾" : "▸"}</span>
          Warianty / oferty konkurencyjne ({alts.length})
        </span>
        <span className="text-[11px] text-[var(--color-text-muted)]">
          nie liczą się do kosztu — do porównania
        </span>
      </button>
      {open && (
        <div className="px-3 pb-3 grid grid-cols-2 gap-2">
          {alts.map((a) => (
            <div
              key={a.id}
              className="border border-[var(--color-border)] bg-[var(--color-bg-surface)] p-2 text-xs"
            >
              <div className="flex items-start justify-between gap-2">
                <div className="font-medium text-[var(--color-text-primary)]">
                  {a.description}
                </div>
                <div className="text-right whitespace-nowrap tabular-nums font-semibold">
                  {money(a.cost)} zł
                </div>
              </div>
              <div className="mt-1 flex items-center gap-1.5 flex-wrap">
                <ContractorTag name={a.contractor_name} />
                <span className="text-[10px] text-[var(--color-text-muted)] tabular-nums">
                  {money(a.quantity)} {a.unit} × {money(a.rate)}
                </span>
              </div>
              <div className="mt-1 flex flex-wrap gap-1">
                {a.coverages.map((c) => (
                  <span
                    key={c.id}
                    className="bg-[var(--color-bg-elevated)] px-1.5 py-0.5 text-[10px]"
                  >
                    poz. {c.position_ordinal}
                  </span>
                ))}
              </div>
              {canEdit && (
                <div className="mt-1.5 flex items-center gap-2">
                  <button
                    className="text-[11px] text-[var(--color-primary)] hover:underline font-medium"
                    title="Ustaw jako aktywne (zacznie liczyć się do kosztu; pamiętaj oznaczyć konkurencyjną aktywną jako wariant)"
                    onClick={() =>
                      mut.patchAssignment.mutate({ aid: a.id, is_alternative: false })
                    }
                  >
                    → ustaw jako aktywne
                  </button>
                  <button
                    className="text-[11px] text-red-500 hover:underline"
                    onClick={() => mut.deleteAssignment.mutate(a.id)}
                  >
                    usuń
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Manual add ───────────────────────────────────────────────────────────────
function AddAssignment({
  sid,
  positions,
  mut,
}: {
  sid: string;
  positions: FlatPos[];
  mut: Mut;
}) {
  const { data: items = [] } = useScenarioLineItems(sid);
  const [liId, setLiId] = useState("");
  const [qty, setQty] = useState("");
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [useHigh, setUseHigh] = useState(false);
  const selected = items.find((li) => li.id === liId);
  const hasHigh = selected?.high_priority_rate != null;
  useEffect(() => {
    if (!hasHigh) setUseHigh(false);
  }, [hasHigh]);
  if (items.length === 0)
    return (
      <p className="text-[11px] text-[var(--color-text-muted)] mt-2">
        Brak pozycji cennika w wybranych umowach ramowych.
      </p>
    );
  return (
    <div className="mt-3 card p-3 text-xs">
      <div className="section-label mb-2">Dodaj przypisanie ręcznie</div>
      <div className="flex flex-wrap items-center gap-2">
        <select
          value={liId}
          onChange={(e) => setLiId(e.target.value)}
          className="border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-1.5 py-1 max-w-[340px]"
        >
          <option value="">— pozycja cennika podwykonawcy —</option>
          {items.map((li) => (
            <option key={li.id} value={li.id}>
              [{li.contractor_name}] {li.name} ({li.unit}) — {money(li.base_rate)} zł
            </option>
          ))}
        </select>
        <input
          value={qty}
          onChange={(e) => setQty(e.target.value)}
          placeholder="ilość"
          className="w-20 border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-1.5 py-1"
        />
        {hasHigh && (
          <label
            className="inline-flex items-center gap-1 text-[11px] text-[var(--color-text-secondary)]"
            title="Użyj stawki priorytetowej z umowy ramowej zamiast bazowej"
          >
            <input
              type="checkbox"
              checked={useHigh}
              onChange={(e) => setUseHigh(e.target.checked)}
            />
            stawka priorytetowa ({money(selected!.high_priority_rate)} zł)
          </label>
        )}
        <Button
          variant="secondary"
          size="sm"
          disabled={!liId || mut.addAssignment.isPending}
          onClick={() =>
            mut.addAssignment.mutate(
              {
                sid,
                line_item_type_id: liId,
                covered_position_ids: [...picked],
                quantity: qty.trim() || undefined,
                use_high_priority: useHigh || undefined,
              },
              {
                onSuccess: () => {
                  setLiId("");
                  setQty("");
                  setPicked(new Set());
                  setUseHigh(false);
                },
              },
            )
          }
        >
          Dodaj
        </Button>
      </div>
      <div className="mt-2 max-h-28 overflow-auto flex flex-wrap gap-x-3 gap-y-1">
        {positions.map((p) => (
          <label
            key={p.id}
            title={p.description}
            className="inline-flex items-center gap-1 text-[10px]"
          >
            <input
              type="checkbox"
              checked={picked.has(p.id)}
              onChange={(e) =>
                setPicked((s) => {
                  const n = new Set(s);
                  e.target.checked ? n.add(p.id) : n.delete(p.id);
                  return n;
                })
              }
            />
            poz. {p.ordinal}
          </label>
        ))}
      </div>
    </div>
  );
}

/**
 * Wybór umów ramowych (= podwykonawców) do scenariusza — rozwijane GRUPY umów
 * (np. „budowlanka kujawsko-pomorskie"), a w nich dopiero podwykonawcy.
 * Umowa w kilku grupach pojawia się w każdej z nich; bez grupy — w koszyku
 * „Bez przypisanej grupy" (zawsze na końcu). Nagłówek grupy zaznacza/odznacza
 * całą grupę naraz. Grupy zarządzane na kartach umów ramowych.
 */
function AgreementPicker({
  value,
  onChange,
}: {
  value: string[];
  onChange: (ids: string[]) => void;
}) {
  const { data: agreements = [] } = useFrameworkAgreements();
  const buckets = useMemo(() => groupRows(agreements), [agreements]);
  // Rozwinięte: domyślnie grupy, w których coś już wybrano (przy edycji), inaczej
  // wszystkie zwinięte — przy kilkudziesięciu umowach lista bez tego nie mieści się na ekranie.
  const [open, setOpen] = useState<Set<string> | null>(null);
  const opened =
    open ??
    new Set(buckets.filter((b) => b.rows.some((r) => value.includes(r.id))).map((b) => b.key));
  const toggleOpen = (key: string) =>
    setOpen((prev) => {
      const n = new Set(prev ?? opened);
      n.has(key) ? n.delete(key) : n.add(key);
      return n;
    });
  if (agreements.length === 0)
    return (
      <p className="text-[11px] text-amber-600">Brak aktywnych umów ramowych z cennikiem.</p>
    );
  return (
    <div className="space-y-1.5">
      {buckets.map((b) => {
        const ids = b.rows.map((r) => r.id);
        const picked = ids.filter((id) => value.includes(id)).length;
        const all = picked === ids.length;
        const isOpen = opened.has(b.key);
        return (
          <div key={b.key} className="border border-[var(--color-border)]">
            <div className="flex items-center gap-2 px-2 py-1 bg-[var(--color-bg-elevated)]">
              <button
                type="button"
                onClick={() => toggleOpen(b.key)}
                className="flex-1 flex items-center gap-1.5 text-left text-[11px] font-medium text-[var(--color-text-primary)]"
                aria-expanded={isOpen}
              >
                <span className="text-[var(--color-text-muted)] w-3 inline-block">
                  {isOpen ? "▾" : "▸"}
                </span>
                <span className={b.key === NO_GROUP_KEY ? "italic" : ""}>{b.name}</span>
                <span className="text-[var(--color-text-muted)] font-normal tabular-nums">
                  {picked > 0 ? `${picked}/${ids.length}` : ids.length}
                </span>
              </button>
              <button
                type="button"
                className="text-[10px] text-[var(--color-primary)] hover:underline whitespace-nowrap"
                onClick={() =>
                  onChange(
                    all
                      ? value.filter((x) => !ids.includes(x))
                      : [...value, ...ids.filter((id) => !value.includes(id))],
                  )
                }
                title={all ? "Odznacz całą grupę" : "Zaznacz całą grupę"}
              >
                {all ? "odznacz wszystkie" : "zaznacz wszystkie"}
              </button>
            </div>
            {isOpen && (
              <div className="flex flex-wrap gap-2 p-2">
                {b.rows.map((ag) => {
                  const on = value.includes(ag.id);
                  return (
                    <button
                      key={ag.id}
                      type="button"
                      onClick={() =>
                        onChange(on ? value.filter((x) => x !== ag.id) : [...value, ag.id])
                      }
                      className={`text-[11px] px-2 py-1 border text-left ${
                        on
                          ? "border-[var(--color-primary)] bg-[var(--color-primary-dim)] text-[var(--color-primary)]"
                          : "border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-text-primary)]"
                      }`}
                      title={ag.title}
                    >
                      <span className="font-medium">{ag.contractor_name}</span>
                      <span className="text-[var(--color-text-muted)]">
                        {" "}
                        · {ag.line_item_count} poz.
                      </span>
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Verbal framework-agreement rule (reguła słowna) ─────────────────────────
function RuleTextPanel({
  sc,
  mut,
  canEdit,
}: {
  sc: SubcontractorScenario;
  mut: Mut;
  canEdit: boolean;
}) {
  const [text, setText] = useState(sc.ai_rule_text);
  const [open, setOpen] = useState(!!sc.ai_rule_text);
  useEffect(() => setText(sc.ai_rule_text), [sc.ai_rule_text]);
  const dirty = text !== sc.ai_rule_text;
  // Nothing written + nothing to write → the whole panel is noise for a viewer.
  if (!canEdit && !sc.ai_rule_text) return null;
  return (
    <div className="card p-3 space-y-2">
      <button
        className="section-label flex items-center gap-1.5"
        onClick={() => setOpen((o) => !o)}
      >
        <span>{open ? "▾" : "▸"}</span>
        Reguła ad-hoc dla AI (tylko ta wycena)
        {sc.ai_rule_text && !open && (
          <span className="text-[10px] text-[var(--color-primary)] font-normal">· ustawiona</span>
        )}
      </button>
      {open && !canEdit && (
        <p className="text-xs whitespace-pre-wrap text-[var(--color-text-secondary)]">
          {sc.ai_rule_text}
        </p>
      )}
      {open && canEdit && (
        <>
          <p className="text-[11px] text-[var(--color-text-secondary)]">
            Stałe deale wpisuj jako <strong>pozycje słowne na umowie ramowej</strong> podwykonawcy
            (są reużywane w każdej wycenie). Tutaj tylko jednorazowy, ad-hoc opis dla tej wyceny —
            AI wyceni objęte pozycje <strong>formułą od naszej wartości ofertowej</strong> (np.
            „minus 5%” → ×0,95). Dojazd „od X km” utworzy osobną pozycję dojazdu.
          </p>
          <textarea
            value={text}
            onChange={(e) => setText(e.target.value)}
            rows={3}
            placeholder="np. Robi nam roboty około-elektryczne (podłączenia, zarobienia końców, mufy, pomiary) na stawkach SEKOCENBUD średnich, narzut 68%, minus 5% od naszej wartości. Dojazd darmowy do 50 km, dalej per km."
            className="w-full text-xs border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-2 py-1.5"
          />
          <div className="flex items-center gap-2">
            <Button
              size="sm"
              variant="secondary"
              disabled={!dirty}
              loading={mut.update.isPending}
              onClick={() => mut.update.mutate({ sid: sc.id, ai_rule_text: text })}
            >
              Zapisz regułę
            </Button>
            {sc.ai_rule_text && (
              <button
                className="text-[11px] text-red-500 hover:underline"
                onClick={() => {
                  setText("");
                  mut.update.mutate({ sid: sc.id, ai_rule_text: "" });
                }}
              >
                wyczyść
              </button>
            )}
            <span className="text-[10px] text-[var(--color-text-muted)]">
              po zapisaniu kliknij „Dobierz AI”
            </span>
          </div>
        </>
      )}
    </div>
  );
}

// ── Build location + travel (dojazd) ────────────────────────────────────────
function BuildLocationPanel({
  sc,
  mut,
  canEdit,
}: {
  sc: SubcontractorScenario;
  mut: Mut;
  canEdit: boolean;
}) {
  const { data: contractors = [] } = useScenarioContractors(sc.id);
  const [label, setLabel] = useState(sc.build_label);
  useEffect(() => setLabel(sc.build_label), [sc.build_label]);
  const lat = sc.build_lat != null ? Number(sc.build_lat) : null;
  const lng = sc.build_lng != null ? Number(sc.build_lng) : null;
  const [baseCity, setBaseCity] = useState("");
  const [baseLabel, setBaseLabel] = useState("");
  const [baseFor, setBaseFor] = useState<string | null>(null);
  const [query, setQuery] = useState("");
  const [searching, setSearching] = useState(false);
  const [hits, setHits] = useState<BuildLocationGeocodeHit[] | null>(null);
  const [focus, setFocus] = useState<{ lat: number; lng: number } | null>(null);
  const pickHit = (h: BuildLocationGeocodeHit) => {
    setHits(null);
    setLabel(h.label);
    setFocus({ lat: h.lat, lng: h.lng });
    mut.buildLocation.mutate({
      sid: sc.id,
      lat: h.lat.toFixed(6),
      lng: h.lng.toFixed(6),
      label: h.label,
    });
  };
  const runSearch = async () => {
    const q = query.trim();
    if (!q || searching) return;
    setSearching(true);
    try {
      const results = await geocodeBuildLocation(q);
      if (results.length === 1) pickHit(results[0]);
      else setHits(results);
    } catch {
      setHits([]);
    } finally {
      setSearching(false);
    }
  };
  const inp =
    "border border-[var(--color-border)] bg-[var(--color-bg-surface)] px-1.5 py-0.5";
  return (
    <div className="card p-3 space-y-2">
      <div className="section-label">Lokalizacja budowy + dojazd</div>
      <p className="text-[11px] text-[var(--color-text-secondary)]">
        {canEdit
          ? "Kliknij na mapie miejsce budowy lub wyszukaj adres / identyfikator działki. Dojazd liczony z najbliższej bazy wypadowej podwykonawcy (lub pełnego adresu siedziby) trasą OSM. Bazę we wsi wpisz z kodem pocztowym i numerem (np. „62-613 Nowa Wieś 12”), bo sama nazwa wsi jest niejednoznaczna. Powyżej 200 km — jawne ostrzeżenie."
          : "Dojazd liczony z najbliższej bazy wypadowej podwykonawcy (lub pełnego adresu siedziby) trasą OSM. Bazę we wsi wpisz z kodem pocztowym i numerem (np. „62-613 Nowa Wieś 12”), bo sama nazwa wsi jest niejednoznaczna. Powyżej 200 km — jawne ostrzeżenie."}
      </p>
      {canEdit && (
        <div className="flex items-center gap-2 text-xs">
          <input
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                e.preventDefault();
                runSearch();
              }
            }}
            placeholder="adres (np. Orchowo, Szkolna 5) lub TERYT działki (np. 220401_1.0008.2/19)"
            className={`flex-1 min-w-[220px] ${inp}`}
          />
          <Button size="sm" onClick={runSearch} disabled={searching || !query.trim()}>
            {searching ? "Szukam…" : "Szukaj"}
          </Button>
        </div>
      )}
      {hits != null &&
        (hits.length === 0 ? (
          <p className="text-[11px] text-amber-700">
            Brak wyników — sprawdź pisownię (format: „Miejscowość, ulica numer”) albo
            pełny TERYT działki.
          </p>
        ) : (
          <ul className="text-xs border border-[var(--color-border)] divide-y divide-[var(--color-border)] max-h-40 overflow-y-auto">
            {hits.map((h, i) => (
              <li key={`${h.lat},${h.lng},${i}`}>
                <button
                  className="w-full text-left px-2 py-1 hover:bg-[var(--color-bg-surface)]"
                  onClick={() => pickHit(h)}
                >
                  {h.kind === "parcel" ? "▦ " : "⌖ "}
                  {h.label}
                </button>
              </li>
            ))}
          </ul>
        ))}
      <BuildLocationMap
        lat={lat}
        lng={lng}
        focus={focus}
        onPick={(la, ln) => {
          if (!canEdit) return;
          mut.buildLocation.mutate({
            sid: sc.id,
            lat: String(la),
            lng: String(ln),
            label,
          });
        }}
      />
      <div className="flex items-center gap-2 text-xs flex-wrap">
        {canEdit ? (
          <input
            value={label}
            onChange={(e) => setLabel(e.target.value)}
            onBlur={() => {
              if (lat != null && label !== sc.build_label)
                mut.buildLocation.mutate({
                  sid: sc.id,
                  lat: sc.build_lat,
                  lng: sc.build_lng,
                  label,
                });
            }}
            placeholder="opis lokalizacji (np. Kraków, ul. X)"
            className={`flex-1 min-w-[180px] ${inp}`}
          />
        ) : (
          sc.build_label && (
            <span className="flex-1 min-w-[180px]">{sc.build_label}</span>
          )
        )}
        {lat != null && lng != null && (
          <>
            <span className="text-[var(--color-text-muted)] tabular-nums">
              {lat.toFixed(5)}, {lng.toFixed(5)}
            </span>
            {canEdit && (
              <button
                className="text-red-500 hover:underline"
                onClick={() =>
                  mut.buildLocation.mutate({ sid: sc.id, lat: null, lng: null })
                }
              >
                wyczyść
              </button>
            )}
          </>
        )}
      </div>
      {lat == null &&
        (canEdit ? (
          <p className="text-[11px] text-amber-700">
            ⚠ Wskaż lokalizację budowy — bez niej dojazd nie zostanie policzony, a może
            wpłynąć na cenę.
          </p>
        ) : (
          <p className="text-[11px] text-amber-700">
            ⚠ Autor nie wskazał lokalizacji budowy — dojazd nie został policzony.
          </p>
        ))}

      {contractors.length > 0 && (
        <div className="border-t border-[var(--color-border)] pt-2">
          <div className="section-label mb-1">
            Podwykonawcy — bazy wypadowe i dojazd
          </div>
          <ul className="text-xs divide-y divide-[var(--color-border)]">
            {contractors.map((ct) => (
              <li key={ct.id} className="py-1.5">
                <div className="flex justify-between gap-2">
                  <span className="font-medium">{ct.name}</span>
                  <span className="tabular-nums whitespace-nowrap">
                    {ct.travel_km != null ? (
                      <span className={ct.travel_km > 200 ? "text-red-600 font-medium" : ""}>
                        {ct.travel_km} km{ct.travel_base ? ` · ${ct.travel_base}` : ""}
                      </span>
                    ) : (
                      <span className="text-[var(--color-text-muted)]">
                        — (wskaż budowę)
                      </span>
                    )}
                  </span>
                </div>
                <div className="text-[10px] text-[var(--color-text-muted)] flex flex-wrap gap-1 items-center mt-0.5">
                  bazy:
                  {ct.bases.length === 0 && (
                    <span>brak → siedziba: {ct.registered_city || "—"}</span>
                  )}
                  {ct.bases.map((b) => (
                    <span key={b.id} className="bg-[var(--color-bg-elevated)] px-1.5 py-0.5">
                      {b.label || b.city} ({b.city})
                      {canEdit && (
                        <button
                          className="ml-1 text-red-500"
                          onClick={() => mut.deleteBase.mutate(b.id)}
                        >
                          ×
                        </button>
                      )}
                    </span>
                  ))}
                  {canEdit && (
                    <button
                      className="text-[var(--color-primary)] hover:underline"
                      onClick={() => setBaseFor(baseFor === ct.id ? null : ct.id)}
                    >
                      + baza
                    </button>
                  )}
                </div>
                {canEdit && baseFor === ct.id && (
                  <div className="mt-1 flex items-center gap-1">
                    <input
                      value={baseLabel}
                      onChange={(e) => setBaseLabel(e.target.value)}
                      placeholder="nazwa (opc.)"
                      className={`w-28 text-[10px] ${inp}`}
                    />
                    <input
                      value={baseCity}
                      onChange={(e) => setBaseCity(e.target.value)}
                      placeholder="miejscowość"
                      title="Miasto albo pełny adres. Dla wsi o częstej nazwie podaj kod pocztowy i numer, np. „62-613 Nowa Wieś 12” — sama nazwa jest niejednoznaczna i baza zostanie bez odległości."
                      className={`w-28 text-[10px] ${inp}`}
                    />
                    <button
                      className="text-[10px] text-[var(--color-primary)] hover:underline"
                      onClick={() => {
                        if (baseCity.trim())
                          mut.addBase.mutate(
                            {
                              contractorId: ct.id,
                              label: baseLabel.trim(),
                              city: baseCity.trim(),
                            },
                            {
                              onSuccess: () => {
                                setBaseLabel("");
                                setBaseCity("");
                                setBaseFor(null);
                              },
                            },
                          );
                      }}
                    >
                      dodaj
                    </button>
                  </div>
                )}
              </li>
            ))}
          </ul>
          {canEdit && (
            <p className="mt-1 text-[10px] text-[var(--color-text-muted)]">
              Po dodaniu/zmianie bazy kliknij ponownie lokalizację budowy lub „Dobierz AI",
              aby przeliczyć dojazd.
            </p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main section ─────────────────────────────────────────────────────────────
export function SubcontractorSection({
  job,
  canEdit,
}: {
  job: EstimateJobDetail;
  // Author/CEO only. A shared valuation shows the scenario + margin read-only.
  canEdit: boolean;
}) {
  const { data: scenarios = [] } = useScenarios(job.id);
  const mut = useScenarioMutations(job.id);
  const [selId, setSelId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [newAgreements, setNewAgreements] = useState<string[]>([]);
  const [editingAgreements, setEditingAgreements] = useState(false);

  const positions: FlatPos[] = useMemo(
    () =>
      job.estimates.flatMap((e) =>
        e.positions.map((p) => ({
          id: p.id,
          ordinal: p.ordinal,
          description: p.description,
          category: p.category,
        })),
      ),
    [job],
  );

  useEffect(() => {
    if (scenarios.length && !scenarios.some((s) => s.id === selId)) {
      setSelId((scenarios.find((s) => s.is_primary) ?? scenarios[0]).id);
    }
  }, [scenarios, selId]);

  const selected: SubcontractorScenario | undefined = scenarios.find((s) => s.id === selId);
  const snap = selected?.cost_snapshot as SubCostSnapshot | undefined;
  const active = (selected?.assignments ?? []).filter((a) => !a.is_alternative);
  const alts = (selected?.assignments ?? []).filter((a) => a.is_alternative);

  return (
    <div className="card p-4">
      <div className="flex items-center justify-between gap-3 mb-3">
        <h2 className="section-label">
          Podwykonawcy — ile nas skasują (warstwa B) + marża
        </h2>
        {canEdit && (
          <Button variant="secondary" size="sm" onClick={() => setCreating((c) => !c)}>
            + Nowy scenariusz
          </Button>
        )}
      </div>

      {creating && canEdit && (
        <div className="mb-3 card p-3 space-y-2">
          <div className="text-xs text-[var(--color-text-secondary)]">
            Wybierz zbiór podwykonawców (umów ramowych) do tego scenariusza:
          </div>
          <AgreementPicker value={newAgreements} onChange={setNewAgreements} />
          <Button
            size="sm"
            disabled={mut.create.isPending}
            onClick={() =>
              mut.create.mutate(
                { name: `Scenariusz ${scenarios.length + 1}`, agreement_ids: newAgreements },
                {
                  onSuccess: (sc) => {
                    setSelId(sc.id);
                    setCreating(false);
                    setNewAgreements([]);
                  },
                },
              )
            }
          >
            Utwórz scenariusz
          </Button>
        </div>
      )}

      {scenarios.length === 0 && !creating && (
        <p className="text-sm text-[var(--color-text-muted)]">
          {canEdit
            ? "Brak scenariuszy. Utwórz scenariusz, wybierz umowy ramowe i dobierz pozycje (AI lub ręcznie), aby policzyć koszt podwykonawców i marżę."
            : "Autor nie utworzył jeszcze żadnego scenariusza podwykonawczego."}
        </p>
      )}

      {scenarios.length > 0 && (
        <div className="flex flex-wrap items-center gap-2 mb-3">
          {scenarios.map((s) => (
            <button
              key={s.id}
              onClick={() => setSelId(s.id)}
              className={`text-xs px-2.5 py-1 border ${
                s.id === selId
                  ? "border-[var(--color-primary)] text-[var(--color-primary)] bg-[var(--color-primary-dim)]"
                  : "border-[var(--color-border)] text-[var(--color-text-secondary)] hover:border-[var(--color-text-primary)]"
              }`}
            >
              {s.name}
              {s.is_primary && " ★"}
            </button>
          ))}
          <span className="text-[10px] text-[var(--color-text-muted)] ml-1">
            (porównaj różne zbiory podwykonawców jako osobne scenariusze)
          </span>
        </div>
      )}

      {selected && (
        <div className="space-y-4">
          {canEdit && (
            <div className="flex flex-wrap items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                loading={mut.aiMatch.isPending}
                onClick={() =>
                  mut.aiMatch.mutate(selected.id, {
                    onSuccess: (r) => {
                      if (r.result?.need_build_location)
                        window.alert(
                          "Najpierw wskaż lokalizację budowy na mapie — dojazd zależy od odległości.",
                        );
                      else if (r.result?.error) window.alert(r.result.error);
                    },
                  })
                }
                title="AI dobierze pozycje cennika podwykonawców do naszych pozycji (1 sub może pokryć kilka KNNR; konkurencyjne oferty oznaczy jako warianty)"
              >
                Dobierz AI
              </Button>
              <button
                className="text-[11px] text-[var(--color-primary)] hover:underline"
                onClick={() => setEditingAgreements((e) => !e)}
              >
                umowy ramowe ({selected.agreement_ids.length})
              </button>
              <button
                className="text-[11px] text-red-500 hover:underline ml-auto"
                onClick={() => {
                  mut.remove.mutate(selected.id);
                  setSelId(null);
                }}
              >
                usuń scenariusz
              </button>
            </div>
          )}

          {canEdit && editingAgreements && (
            <div className="card p-2">
              <AgreementPicker
                value={selected.agreement_ids}
                onChange={(ids) => mut.update.mutate({ sid: selected.id, agreement_ids: ids })}
              />
            </div>
          )}

          <RuleTextPanel sc={selected} mut={mut} canEdit={canEdit} />

          <BuildLocationPanel sc={selected} mut={mut} canEdit={canEdit} />

          {snap && <MarginPanel snap={snap} />}

          <div className="card overflow-x-auto">
            <table className="w-full text-xs">
              <thead className="text-[var(--color-text-muted)] bg-[var(--color-bg-surface)]">
                <tr>
                  <th className="px-2 py-2 text-left font-medium">Pozycja podwykonawcy</th>
                  <th className="px-2 py-2 text-left font-medium">Ilość × stawka</th>
                  <th className="px-2 py-2 text-right font-medium">Koszt</th>
                  <th className="px-2 py-2 text-left font-medium">Pokrywa nasze poz.</th>
                  <th className="px-2 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {active.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-2 py-3 text-[var(--color-text-muted)]">
                      {canEdit
                        ? "Brak aktywnych przypisań. Użyj „Dobierz AI\" albo dodaj ręcznie poniżej."
                        : "Brak aktywnych przypisań w tym scenariuszu."}
                    </td>
                  </tr>
                )}
                {active.map((a) => (
                  <AssignmentRow
                    key={a.id}
                    a={a}
                    positions={positions}
                    mut={mut}
                    canEdit={canEdit}
                  />
                ))}
              </tbody>
            </table>
          </div>

          <AlternativesPanel alts={alts} mut={mut} canEdit={canEdit} />

          {canEdit && (
            <AddAssignment sid={selected.id} positions={positions} mut={mut} />
          )}
        </div>
      )}
    </div>
  );
}
